import { PAN_HEIGHT, PAN_PROJECTION } from 'helpers/constants/attributes';

/**
 * Groups the raw values of a measurement term facet (e.g. pan projection "315", "337", "345"...)
 * into a few fixed buckets, so shoppers pick "551-650mm" instead of ticking every size.
 * Selecting a bucket refines every raw value that falls inside it, so no index changes are needed.
 */
export interface MeasurementBucket {
  key: string;
  label: string;
  /** Inclusive lower bound. */
  min: number;
  /** Exclusive upper bound — keeps decimals (e.g. 395.5) from falling between buckets. */
  max: number;
}

export const PAN_PROJECTION_BUCKETS: MeasurementBucket[] = [
  { key: 'lt-551', label: '< 550mm', min: -Infinity, max: 551 },
  { key: '551-650', label: '551-650mm', min: 551, max: 651 },
  { key: '651-700', label: '651-700mm', min: 651, max: 701 },
  { key: 'gt-700', label: '> 701mm', min: 701, max: Infinity },
];

export const PAN_HEIGHT_BUCKETS: MeasurementBucket[] = [
  { key: 'lt-395', label: '< 395mm', min: -Infinity, max: 396 },
  { key: '396-419', label: '396 - 419mm', min: 396, max: 420 },
  { key: '420-439', label: '420 - 439mm', min: 420, max: 440 },
  { key: 'gt-440', label: '> 440mm', min: 440, max: Infinity },
];

const BUCKETS_BY_ATTRIBUTE: Record<string, MeasurementBucket[]> = {
  [PAN_PROJECTION]: PAN_PROJECTION_BUCKETS,
  [PAN_HEIGHT]: PAN_HEIGHT_BUCKETS,
};

/** Bucket definitions for a facet attribute path (e.g. `attributes.pan-projection-size`), if any. */
export const getMeasurementBuckets = (attribute: string): MeasurementBucket[] | undefined =>
  BUCKETS_BY_ATTRIBUTE[attribute.split('.').pop() ?? ''];

/** The bucket a raw facet value belongs to — tolerates values like "315" or "315mm". */
export const findMeasurementBucket = (buckets: MeasurementBucket[], value: unknown) => {
  const amount = parseFloat(String(value).split('>').pop() ?? '');
  if (!Number.isFinite(amount)) return undefined;
  return buckets.find(({ min, max }) => amount >= min && amount < max);
};

export interface BucketedTermItem<T> {
  bucket: MeasurementBucket;
  items: T[];
  isRefined: boolean;
}

/** Groups refinement-list items into their buckets, in bucket order, dropping empty buckets. */
export const groupTermItemsByBucket = <T extends { value: string; isRefined: boolean }>(
  buckets: MeasurementBucket[],
  items: T[],
): BucketedTermItem<T>[] =>
  buckets
    .map((bucket) => {
      const bucketItems = items.filter((item) => findMeasurementBucket(buckets, item.value) === bucket);
      return { bucket, items: bucketItems, isRefined: bucketItems.some((item) => item.isRefined) };
    })
    .filter(({ items }) => items.length > 0);

export interface BucketedRefinement<T> {
  /** First refinement of the group — used for the chip's attribute/key. */
  refinement: T;
  /** Every refinement the chip stands for; removing the chip removes all of them. */
  members: T[];
  /** Set when the refinements were collapsed into a bucket chip. */
  bucketLabel?: string;
}

/** Collapses selected bucketed values into one chip per bucket; other refinements pass through. */
export const groupRefinementsByBucket = <T extends { attribute: string; value?: unknown }>(
  refinements: T[],
): BucketedRefinement<T>[] => {
  const result: BucketedRefinement<T>[] = [];
  const bucketGroups = new Map<string, BucketedRefinement<T>>();

  refinements.forEach((refinement) => {
    const buckets = getMeasurementBuckets(refinement.attribute);
    const bucket = buckets && findMeasurementBucket(buckets, refinement.value);
    if (!bucket) {
      result.push({ refinement, members: [refinement] });
      return;
    }
    const groupKey = `${refinement.attribute}::${bucket.key}`;
    const existing = bucketGroups.get(groupKey);
    if (existing) {
      existing.members.push(refinement);
      return;
    }
    const group = { refinement, members: [refinement], bucketLabel: bucket.label };
    bucketGroups.set(groupKey, group);
    result.push(group);
  });

  return result;
};
